import {
  ListClientsForAccountApiArg,
  V1PagingMeta,
  V1Relationships,
  useListClientsForAccountQuery,
} from "@generated";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import type { BaseRelationship } from "@baseTypes";
import type { UserDefinedField, Client } from "../types";

export interface HookArgs {
  page?: ListClientsForAccountApiArg["page"];
  filter?: ListClientsForAccountApiArg["filter"];
}

const useListClients = ({ page, filter }: HookArgs) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { data, error, isLoading, refetch } = useListClientsForAccountQuery({
    slug: accountSlug,
    include: ["rate-table"],
    page,
    filter,
  });

  let clients: Client[] = [];
  let customError: string | null = null;
  let pageMeta = {
    recordCount: 0,
    pageCount: 0,
  };

  if (data?.data) {
    try {
      clients = data.data.map((client) => {
        if (!client.id) {
          throw new Error("Missing client.id");
        }

        return {
          id: client.id,
          name: client.attributes?.name || "",
          msaDate: client.attributes?.["msa-date"],
          domain: client.attributes?.domain,
          active: client.attributes?.active,
          userDefinedFields: client.attributes?.[
            "user-defined-fields"
          ] as unknown as UserDefinedField[] | undefined,
        };
      });

      if (!data.meta) {
        throw new Error("Missing data.meta");
      }

      if (!data.meta["record-count"]) {
        throw new Error('Missing data.meta["record-count"]');
      }

      if (!data.meta["page-count"]) {
        throw new Error('Missing data.meta["page-count"]');
      }
      pageMeta = {
        recordCount: data.meta["record-count"],
        pageCount: data.meta["page-count"],
      };
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }

  return { clients, error: error || customError, isLoading, pageMeta, refetch };
};

export default useListClients;
